import {
  camelCase,
  isArray,
  isEmpty,
  isNil,
  isObject,
  isString,
  omitBy,
  reduce,
} from "lodash";
import moment, { Moment } from "moment";
import { SCHEDULER_ROUNDING } from "@sonder/ui/source/styled/components/ScheduleLayer/constants";
import { roundToInterval } from "@sonder/ui/source/styled/components/ScheduleLayer/helpers";
import { OptionType } from "@sonder/ui/source/styled/types";
import { UnitConsumables } from "core/queries/GetUnitConsumables";
import {
  SearchForTaskContextsItem,
  SearchForTaskContextsResourceFeatures,
} from "core/queries/SearchForTaskContexts";
import { SearchForTaskSubjectsItem } from "core/queries/SearchForTaskSubjects";
import { snakeToCamelCase } from "core/utils/snakeToCamelCase";
import { titlelizeCase } from "core/utils/titlelizeCase";
import {
  SerializedTaskContextFeatures,
  SerializedTreeStructureTaskContext,
} from "tasks/components/TaskContexts";
import {
  GuidedFlowState,
  initialGuidedFlowState,
} from "tasks/components/TaskGuidedFlow";
import {
  CleaningWindow,
  EarlyCheckIn,
  FilterDropOffLocation,
  GuestAlreadyAware,
  GuestReceivedPreviousWarning,
  GuestUrgency,
  HoldSecurityDeposit,
  Inspection,
  Issue,
  LateCheckOut,
  ReasonForBan,
  ReasonForEjection,
  ReasonForRelocation,
  Request,
  Result,
  SecurityOnsite,
  SourceOfRelocationRequest,
  StructuredMetadataBooking,
  TaskContextSubjectTypeEnum,
  TaskLocationPlaceEnum,
  TaskRecurrenceSettingsInput,
  TrustAndSafety,
  TypeEnum,
} from "typings/globalGQLTypes";

export const dropOffLocations: OptionType[] = [
  {
    value: FilterDropOffLocation.unit,
    displayName: titlelizeCase(FilterDropOffLocation.unit),
  },
  {
    value: FilterDropOffLocation.office,
    displayName: titlelizeCase(FilterDropOffLocation.office),
  },
];

export const guestUrgency: OptionType[] = [
  { value: GuestUrgency.low, displayName: titlelizeCase(GuestUrgency.low) },
  {
    value: GuestUrgency.medium,
    displayName: titlelizeCase(GuestUrgency.medium),
  },
  { value: GuestUrgency.high, displayName: titlelizeCase(GuestUrgency.high) },
];

export const guestAlreadyAware: OptionType[] = [
  {
    value: GuestAlreadyAware.no,
    displayName: titlelizeCase(GuestAlreadyAware.no),
  },
  {
    value: GuestAlreadyAware.yes,
    displayName: titlelizeCase(GuestAlreadyAware.yes),
  },
];

export const securityOnsite: OptionType[] = [
  {
    value: SecurityOnsite.no,
    displayName: titlelizeCase(SecurityOnsite.no),
  },
  {
    value: SecurityOnsite.yes,
    displayName: titlelizeCase(SecurityOnsite.yes),
  },
];

export const guestReceivedPreviousWarning: OptionType[] = [
  {
    value: GuestReceivedPreviousWarning.no,
    displayName: titlelizeCase(GuestReceivedPreviousWarning.no),
  },
  {
    value: GuestReceivedPreviousWarning.yes,
    displayName: titlelizeCase(GuestReceivedPreviousWarning.yes),
  },
];

export const reasonForEjection: OptionType[] = Object.keys(
  ReasonForEjection,
).map(reason => ({
  value: reason,
  displayName: titlelizeCase(reason),
}));

export const reasonForBan = Object.keys(ReasonForBan).map(reason => ({
  value: reason,
  displayName: titlelizeCase(reason),
}));

export const sourceOfRelocationRequest = Object.keys(
  SourceOfRelocationRequest,
).map(source => ({
  value: source,
  displayName: titlelizeCase(source),
}));

export const reasonsForRelocation = {
  [SourceOfRelocationRequest.guest]: [
    ReasonForRelocation.accommodating_booking_extension,
    ReasonForRelocation.floor_request,
    ReasonForRelocation.shuffling_unit_does_not_meet_guest_expectations,
    ReasonForRelocation.specific_unit_desired,
    ReasonForRelocation.switch_buildings_locations,
    ReasonForRelocation.to_downgrade_space,
    ReasonForRelocation.to_upgrade_space,
    // Other is put at the bottom to minimize selection
    ReasonForRelocation.other_reason_specify,
  ].map(source => ({
    value: source,
    displayName: titlelizeCase(source),
  })),
  [SourceOfRelocationRequest.sonder]: [
    ReasonForRelocation.access,
    ReasonForRelocation.bed_bug_or_vermin,
    ReasonForRelocation.cleanliness,
    ReasonForRelocation.double_booked,
    ReasonForRelocation.missing_or_broken_item_appliance,
    ReasonForRelocation.need_to_accommodate_ltr,
    ReasonForRelocation.off_boarding,
    ReasonForRelocation.pending_maintenance_repairs,
    ReasonForRelocation.plumbing_hvac_issue,
    // Other is put at the bottom to minimize selection
    ReasonForRelocation.other_reason_specify,
  ].map(source => ({
    value: source,
    displayName: titlelizeCase(source),
  })),
};

export const preferredCommunicationMethod: OptionType[] = [
  { value: "sms", displayName: "SMS" },
  { value: "email", displayName: "Email" },
];

export const communicationMethod: OptionType[] = [
  ...preferredCommunicationMethod,
  { value: "phone_call", displayName: "Phone Call" },
  { value: "ota", displayName: "OTA" },
  { value: "in_person", displayName: "In Person" },
  { value: "other", displayName: "Other" },
];

export const intrastayCleaningConfigTimeSlotDisplayNames = {
  [CleaningWindow.morning]: "Morning (9:00 - 12:00)",
  [CleaningWindow.early_afternoon]: "Early Afternoon (12:00 - 15:00)",
  [CleaningWindow.late_afternoon]: "Late Afternoon (15:00 - 18:00)",
};

export const isPreferredCommunicationsMethod = (method: string) =>
  preferredCommunicationMethod.filter(m => m.value === method).length > 0;

// Used in conversion of data for the actual mutation, wipes out undefined / null fields, including arrays / objects.
// 0 / false are still cool, and "" is not considered empty.
// EXAMPLE:
// { a: 0, b: "", c: undefined, d: false, e: {}, f: [], g: null } => { a: 0, b: "", d: false }
// TODO: Move out to a util file?
export const isEmptyOrNil = (a: any) => {
  if (isString(a)) return !a;

  if (isObject(a) || isArray(a)) return isEmpty(a);

  return isNil(a);
};

const convertDefaultContextFeatureValue = (
  feature: SerializedTaskContextFeatures,
  defaultValue?: any,
) => ({
  [camelCase(feature.name)]: {
    ...feature,
    name: titlelizeCase(snakeToCamelCase(feature.name)),
    defaultValue: defaultValue || feature.defaultValue,
  },
});

const convertDefaultContextFeatureValues = (
  memo: any,
  feature: SerializedTaskContextFeatures,
) => {
  const camelName = camelCase(feature.name);
  switch (camelName) {
    case "ejectionAt":
      return {
        ...memo,
        ...convertDefaultContextFeatureValue(
          feature,
          roundToInterval(SCHEDULER_ROUNDING, moment()),
        ),
      };
    default:
      return {
        ...memo,
        ...convertDefaultContextFeatureValue(feature),
      };
  }
};

// Used in rendering / form value access. This takes in a Task Context and gives you the features keyed by camel-cased name.
// We may want to memo-ize this, the goal is to provide quick, typesafe lookups, so don't over-call this function!
// EXAMPLE:
// [{ name: "foo_bar", notes: "baz"}, ...] => { fooBar: { name: "foo_bar", notes: "baz" } }
export const convertContextToFeatures = (
  context: SlicedSerializedTaskContext,
) =>
  (context?.contextFeatures || context?.contextItemFeatures)?.reduce(
    convertDefaultContextFeatureValues,
    {},
  ) || {};

// Some Features may or may not be mutable to the User depending on the Context.
// This method groups the two types of featurs into distinctive groups.
// _.groupBy would have worked but doesn't handle Objects as well as a reduce will in this case, IMHO.
export const splitFeaturesByMutability = (
  features: TaskContextFeatureMap,
): MutableFeaturesMap =>
  reduce(
    features,
    (memo, feature, key) => {
      const field = feature.mutable
        ? "mutableContextFeatures"
        : "immutableContextFeatures";
      return {
        ...memo,
        [field]: {
          ...memo[field],
          [key]: feature,
        },
      };
    },
    { mutableContextFeatures: {}, immutableContextFeatures: {} },
  );

// PLEASE DOCUMENT WHY YOU ADDED A FEATURE IF YOU HAVE TO ADD A FEATURE HERE!
export const IGNORE_REQUIRED_FEATURES = [
  // Had to add this hack to allow us to filter out consumable context feature since we
  // currently only use to display consumables field.We don't want to send this value to the mutation since its not accepted field.
  // Ideally we need to think about subContextFeatures for consumables.
  "consumable",

  // These were added as `communicationMethod` / `preferredCommunicationMethod` exist on updateTask directly, not in metadata.
  "communicationMethod",
  "preferredCommunicationMethod",
];

export const shouldIgnoreRequiredFeature = (featureKey: string) =>
  !IGNORE_REQUIRED_FEATURES.includes(featureKey);

// Used in the actual mutation, will give you the correct key to store metadata into.
// [⚠️ WARNING!] You must add any new Task Types being supported here.
const convertTypeToMetadataKey = (type?: TypeEnum) => {
  switch (type) {
    case TypeEnum.Tom_Tasks_Booking:
      return "booking";
    case TypeEnum.Tom_Tasks_EarlyCheckIn:
      return "earlyCheckIn";
    case TypeEnum.Tom_Tasks_HoldSecurityDeposit:
      return "holdSecurityDeposit";
    case TypeEnum.Tom_Tasks_Inspection:
      return "inspection";
    case TypeEnum.Tom_Tasks_Issue:
      return "issue";
    case TypeEnum.Tom_Tasks_LateCheckOut:
      return "lateCheckOut";
    case TypeEnum.Tom_Tasks_Request:
      return "request";
    case TypeEnum.Tom_Tasks_TrustAndSafety:
      return "trustAndSafety";
    default:
      return "unsupported_task_type";
  }
};

// Crappy Hack, our `defaultValue` for `quantity` on the Backend is being returned as a String, not a number.
// Ideally, the backend would allow for defaultValue to support a Union of Different Types so it provides the truely
// correct value, otherwise, the defaultValue can be incorrectly co-oerced into a string when the Mutation expects a number.

// If we run into a bug similar to:
// "GraphQL error: Variable $typeStructuredMetadata of type TypeStructuredMetadata was provided invalid value for request.quantity"
// It is possibly this again.
// Add the new field both to INVALID_DEFAULT_VALUE_FIELDS and add a new case in the `switch` statement.
enum INVALID_DEFAULT_VALUE_FIELDS {
  quantity = "quantity",
}
const castInvalidDefaultValuesToRightFormat = (
  typeStructuredMetadata: MetadataState,
) => {
  return Object.keys(typeStructuredMetadata).reduce((result, key) => {
    let val = typeStructuredMetadata[key as keyof MetadataState] as any;
    if (
      Object.values(INVALID_DEFAULT_VALUE_FIELDS).includes(
        key as INVALID_DEFAULT_VALUE_FIELDS,
      )
    )
      switch (key) {
        case INVALID_DEFAULT_VALUE_FIELDS.quantity:
          val = parseInt(val, 10);
          break;
        default:
          /* eslint-disable-next-line no-console */
          console.error(
            `castInvalidDefaultValuesToRightFormat: Add a new case statement for key: ${key}. `,
          );
          break;
      }

    return {
      ...result,
      [key]: val,
    };
  }, {} as MetadataState);
};

// Used to generate the actual mutation variables, should give you the correct input params given the states.
// Checkout the isEmptyOrNil for more. :)
// EXAMPLE:
// ({ assigneeId: undefined, type: "Foo" }, { bar: 'Baz' }) => { type: 'Foo', typeStructuredMetadata: { foo: { bar: 'Baz' } } }
export const convertStateToMutationVariables = (
  sharedTaskState: SharedTaskState,
  metadataState: MetadataState,
) =>
  // TLDR: I didn't want to write us a recursive datamapper yet. This is a great place to override anything, if we must.
  omitBy(
    {
      ...sharedTaskState,
      typeStructuredMetadata: omitBy(
        {
          [convertTypeToMetadataKey(sharedTaskState.type)]: {
            ...omitBy(
              castInvalidDefaultValuesToRightFormat(metadataState),
              isEmptyOrNil,
            ),
          },
        },
        isEmptyOrNil,
      ),
    },
    isEmptyOrNil,
  );

export const convertStateToMutationVariablesAndProvideDefaults = (
  sharedTaskState: SharedTaskState,
  metadataState: MetadataState,
) => {
  const createTaskMutationVariables = convertStateToMutationVariables(
    sharedTaskState,
    metadataState,
  );
  return {
    ...createTaskMutationVariables,
    // Ugh, this is annoying.
    // To make a flexible system that plugs into the mutation, we need to provide some crappy defaults.
    // Ideally the FE will make these defaults more "solid" soon.
    name: (createTaskMutationVariables.name || "Unnamed") as string,
    notes: (createTaskMutationVariables.notes || "") as string,
    subjectId: (createTaskMutationVariables.subjectId ||
      "missing_subject_id") as string,
    type: createTaskMutationVariables.type as TypeEnum,
  };
};

// TODO: BE may provide us this in the future, for now, hardcoded out.
export const canAssign = (taskType?: TypeEnum) => {
  switch (taskType) {
    case TypeEnum.Tom_Tasks_TrustAndSafety:
      return false;
    default:
      return true;
  }
};

export interface SlicedSerializedTaskContext
  extends Omit<SerializedTreeStructureTaskContext, "children"> {}

// We need to connect/map Subject, Result and Task Types for better syncing across the form
// So, this is a custom Subject interface that houses all of them
export interface SubjectTypeConfig {
  bulkTaskConfig?: BulkTaskConfiguration;
  displayName: string;
  resultType: Result;
  subjectType: TaskContextSubjectTypeEnum;
  searchPlaceholder: string;
  locationPlaces: TaskLocationPlaceEnum[];
  validTaskTypes: TypeEnum[];
}

export interface SubjectTypesConfig {
  [subjectType: string]: SubjectTypeConfig;
}

export enum BulkFormTabs {
  Single = "SINGLE",
  Multiple = "MULTIPLE",
}
export interface BulkTaskConfiguration {
  displayName: string;
  searchTitle: string;
  searchPlaceholder: string;
  supportedTabs: BulkFormTabs[];
  parentSubjectType: TaskContextSubjectTypeEnum;
}

// [⚠️ WARNING!] You must add any new Subject Types & Task Types being supported here.
export const subjectTypesConfig: SubjectTypesConfig = {
  [TaskContextSubjectTypeEnum.Unit]: {
    bulkTaskConfig: {
      displayName: "Unit(s)",
      searchTitle: "Select Units",
      searchPlaceholder: "Search units...",
      supportedTabs: [BulkFormTabs.Single, BulkFormTabs.Multiple],
      parentSubjectType: TaskContextSubjectTypeEnum.Building,
    },
    displayName: "Unit",
    locationPlaces: [TaskLocationPlaceEnum.Unit],
    resultType: Result.unit,
    subjectType: TaskContextSubjectTypeEnum.Unit,
    searchPlaceholder: "Search units...",
    validTaskTypes: [
      TypeEnum.Tom_Tasks_Issue,
      TypeEnum.Tom_Tasks_Request,
      TypeEnum.Tom_Tasks_Inspection,
    ],
  },
  [TaskContextSubjectTypeEnum.Inquiry]: {
    displayName: "Inquiry",
    locationPlaces: [
      TaskLocationPlaceEnum.Building,
      TaskLocationPlaceEnum.Unit,
    ],
    resultType: Result.inquiry,
    subjectType: TaskContextSubjectTypeEnum.Inquiry,
    searchPlaceholder: "Search inquiries...",
    validTaskTypes: [
      TypeEnum.Tom_Tasks_Issue,
      TypeEnum.Tom_Tasks_Request,
      TypeEnum.Tom_Tasks_Booking,
      TypeEnum.Tom_Tasks_EarlyCheckIn,
      TypeEnum.Tom_Tasks_LateCheckOut,
      TypeEnum.Tom_Tasks_HoldSecurityDeposit,
      TypeEnum.Tom_Tasks_TrustAndSafety,
    ],
  },
  [TaskContextSubjectTypeEnum.LeaseInquiry]: {
    displayName: "Flex Booking",
    locationPlaces: [TaskLocationPlaceEnum.Unit],
    resultType: Result.lease_inquiry,
    subjectType: TaskContextSubjectTypeEnum.LeaseInquiry,
    searchPlaceholder: "Search flex bookings...",
    validTaskTypes: [
      TypeEnum.Tom_Tasks_Issue,
      TypeEnum.Tom_Tasks_Request,
      TypeEnum.Tom_Tasks_Booking,
      TypeEnum.Tom_Tasks_HoldSecurityDeposit,
      TypeEnum.Tom_Tasks_TrustAndSafety,
    ],
  },
  [TaskContextSubjectTypeEnum.Building]: {
    displayName: "Building",
    locationPlaces: [TaskLocationPlaceEnum.Building],
    resultType: Result.building,
    subjectType: TaskContextSubjectTypeEnum.Building,
    searchPlaceholder: "Search buildings...",
    validTaskTypes: [
      TypeEnum.Tom_Tasks_Inspection,
      TypeEnum.Tom_Tasks_Issue,
      TypeEnum.Tom_Tasks_Request,
    ],
  },
  [TaskContextSubjectTypeEnum.Cuf_Space]: {
    displayName: "Space",
    locationPlaces: [TaskLocationPlaceEnum.Space],
    resultType: Result.space,
    subjectType: TaskContextSubjectTypeEnum.Cuf_Space,
    searchPlaceholder: "Search Spaces...",
    validTaskTypes: [TypeEnum.Tom_Tasks_SpaceClean],
  },
};

export const SubjectTypeAndBulkFormTabLabel = {
  [TaskContextSubjectTypeEnum.Unit]: {
    [BulkFormTabs.Single]: "Single Unit",
    [BulkFormTabs.Multiple]: "Multiple Units",
  },
  [TaskContextSubjectTypeEnum.Building]: null,
  [TaskContextSubjectTypeEnum.Cuf_Space]: null,
  [TaskContextSubjectTypeEnum.Tom_Booking]: null,
  [TaskContextSubjectTypeEnum.Inquiry]: null,
  [TaskContextSubjectTypeEnum.LeaseInquiry]: null,
};

export const initialSubjectType =
  subjectTypesConfig[TaskContextSubjectTypeEnum.Unit];

// These fields do not change when a Task Type is selected.
export interface SharedTaskState {
  assigneeId?: string | null;
  communicationMethod?: string | null;
  dueAt: Moment | null;
  name: string;
  notes: string;
  preferredCommunicationMethod?: string | null;
  priority: number | null;
  recurrenceSettings: TaskRecurrenceSettingsInput | null;
  subjectId: string | null;
  type?: TypeEnum;
  tomTaskContextId: number | null;
}

// If we want to set defaults on any shared state, it would be here.
export const initialSharedTaskState = {
  assigneeId: null,
  communicationMethod: null,
  dueAt: null,
  name: "",
  notes: "",
  preferredCommunicationMethod: null,
  priority: null,
  recurrenceSettings: null,
  subjectId: null,
  type: initialSubjectType.validTaskTypes[0],
  tomTaskContextId: null,
};

export interface BulkFormState {
  parentSubject: SearchForTaskSubjectsItem | null;
  parentSubjectType: SubjectTypeConfig | null;
  subjects: SearchForTaskSubjectsItem[] | null;
  selectedTab: BulkFormTabs;
}

export interface FormState {
  bulkFormState: BulkFormState | null;
  isDispatching: boolean;
  isGuidedFlowOpen: boolean;
  isPreferredWindowBelowThreshold: boolean;
  guidedFlowState: GuidedFlowState;
  preferredRelocationUnit: SearchForTaskSubjectsItem | null;
  fallbackRelocationUnit: SearchForTaskSubjectsItem | null;
  searchedContext: SearchForTaskContextsItem | null;
  subject: SearchForTaskSubjectsItem | null;
  subjectType: SubjectTypeConfig;
  consumables: UnitConsumables[];
}

// Values that are not being used in the API call but required to control form's flow
export const initialFormState: FormState = {
  bulkFormState: null,
  isDispatching: false,
  isGuidedFlowOpen: false,
  isPreferredWindowBelowThreshold: false,
  guidedFlowState: initialGuidedFlowState,
  preferredRelocationUnit: null,
  fallbackRelocationUnit: null,
  searchedContext: null,
  subject: null,
  subjectType: initialSubjectType,
  consumables: [],
};

// Metadata changes depending on the type of task Selected.
// [⚠️ WARNING!] You must add any new Task Types being supported here.
export type MetadataState =
  | EarlyCheckIn
  | Inspection
  | Issue
  | HoldSecurityDeposit
  | LateCheckOut
  | Request
  | StructuredMetadataBooking
  | TrustAndSafety;

// Kept empty initially, as each Task type will have it's own Features to manage.
// The Feature coming from the BE depending on Context includes a "required" / "name" / etc.
export const initialMetadataState: MetadataState = {};

// Used in the actual mutation, this maps Issue to "issue" for Typescript support of the Resource / Features.
export interface TaskContextFeatureMap {
  [key: string]: SearchForTaskContextsResourceFeatures;
}

export interface MutableFeaturesMap {
  mutableContextFeatures: TaskContextFeatureMap;
  immutableContextFeatures: TaskContextFeatureMap;
}

// Singular Task Creation Mutation returns a TaskID if successful.
export type SingleTaskSubmitCallbackMethod = (taskId?: string) => void;
// Bulk Task Creation Mutation returns a JobID, we can query the JobID to see when the Job completes.
export type BulkTaskSubmitCallbackMethod = (jobId?: string) => void;
